import React from 'react';
import {
  Chip,
  Box,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { AbsenceBalanceHistoryDto } from '../../../types/balanceHistory';
import { AbsenceCategory, BalanceChangeType } from '../../../constants/absenceEnums';
import { Column, DataTable } from '../../../components/common/DataTable/DataTable';
import type { SearchableColumn } from '../../../components/common/SearchInputs';

interface BalanceHistoryTableProps {
  history: AbsenceBalanceHistoryDto[];
  showEmployeeColumn?: boolean;
  isLoading?: boolean;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;
  enableSearch?: boolean;
  searchableColumns?: SearchableColumn[];
  onSearchChange?: (filters: Record<string, any>) => void;
}

const getChangeTypeColor = (changeType: BalanceChangeType): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (changeType) {
    case BalanceChangeType.Earned:
    case BalanceChangeType.CarryOver:
      return 'success';
    case BalanceChangeType.Used:
    case BalanceChangeType.Expired:
      return 'error';
    case BalanceChangeType.AdminAdjustment:
      return 'warning';
    default:
      return 'default';
  }
};

const getCategoryColor = (category: AbsenceCategory): 'primary' | 'secondary' | 'info' | 'default' => {
  switch (category) {
    case AbsenceCategory.Vacation:
      return 'primary';
    case AbsenceCategory.SickDays:
      return 'secondary';
    default:
      return 'default';
  }
};

const BalanceHistoryTable: React.FC<BalanceHistoryTableProps> = ({
  history,
  showEmployeeColumn = false,
  isLoading = false,
  page = 1,
  pageSize = 25,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  hasNextPage = false,
  isLoadingMore = false,
  isLoadingRows = false,
  onLoadMore,
  enableSearch = false,
  searchableColumns = [],
  onSearchChange,
}) => {
  const columns: Column[] = [
    {
      field: 'createdAt',
      headerName: 'Date & Time',
      minWidth: 320,
      renderCell: (value) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {format(new Date(value), 'MMM dd, yyyy')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(value), 'HH:mm')}
          </Typography>
        </Box>
      ),
    },
    ...(showEmployeeColumn
      ? [
          {
            field: 'employeeName',
            headerName: 'Employee',
            minWidth: 220,
            renderCell: (value: string) => (
              <Typography variant="body2">{value}</Typography>
            ),
          },
        ]
      : []),
    {
      field: 'absenceCategory',
      headerName: 'Category',
      minWidth: 160,
      renderCell: (_value, record) => (
        <Chip
          label={record.absenceCategoryName}
          color={getCategoryColor(record.absenceCategory)}
          size="small"
        />
      ),
    },
    {
      field: 'changeType',
      headerName: 'Change Type',
      minWidth: 190,
      renderCell: (_value, record) => (
        <Chip
          label={record.changeTypeName}
          color={getChangeTypeColor(record.changeType)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'changeAmount',
      headerName: 'Change Amount',
      renderCell: (value) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          color={value > 0 ? 'success.main' : 'error.main'}
        >
          {value > 0 ? '+' : ''}{value.toFixed(1)}
        </Typography>
      ),
    },
    {
      field: 'balanceBefore',
      headerName: 'Balance Before',
      renderCell: (value) => (
        <Typography variant="body2">{value.toFixed(1)}</Typography>
      ),
    },
    {
      field: 'balanceAfter',
      headerName: 'Balance After',
      renderCell: (value) => (
        <Typography variant="body2" fontWeight="medium">
          {value.toFixed(1)}
        </Typography>
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      renderCell: (value) =>
        value ? (
          <Typography variant="body2" sx={{ maxWidth: 200 }}>
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            —
          </Typography>
        ),
    },
    {
      field: 'createdByUserName',
      headerName: 'Performed By',
      renderCell: (value) =>
        value ? (
          <Typography variant="body2">{value}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            System
          </Typography>
        ),
    },
  ];

  return (
    <DataTable
      items={history}
      columns={columns}
      isLoading={isLoading}
      page={page - 1}
      pageSize={pageSize}
      totalCount={totalCount}
      onPageChange={(newPage) => onPageChange?.(newPage + 1)}
      onPageSizeChange={onPageSizeChange}
      rowsPerPageOptions={[10, 25, 50, 100]}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      isLoadingRows={isLoadingRows}
      onLoadMore={onLoadMore}
      enableSearch={enableSearch}
      searchableColumns={searchableColumns}
      onSearchChange={onSearchChange}
    />
  );
};

export default BalanceHistoryTable;


